import React, { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import AppBar from '@material-ui/core/AppBar'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import { VaccineDto } from 'src/dto/vaccine.dto'
import { Input, InputCheck } from 'src/components'
import { useStyles } from './styles'
import { TAB_LABELS, TabPanelProps, VACCINE_TYPES, VaccineTabsProps, VaccineType } from './types'

const TabPanel: React.FC<TabPanelProps & React.HTMLAttributes<HTMLDivElement>> = ({
  children,
  value,
  index,
  ...rest
}) => (
  <div
    role='tabpanel'
    hidden={value !== index}
    id={`scrollable-auto-tabpanel-${index}`}
    aria-labelledby={`scrollable-auto-tab-${index}`}
    {...rest}
  >
    {value === index && (
      <Box p={3}>
        <Typography>{children}</Typography>
      </Box>
    )}
  </div>
)

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired
}

const getTabA11yProps = (index: number) => ({
  id: `scrollable-auto-tab-${index}`,
  'aria-controls': `scrollable-auto-tabpanel-${index}`
})

const filterVaccinesByType = (list: VaccineDto[], type?: VaccineType): VaccineDto[] => {
  if (!type) return list
  return list.filter((item) => item.data.tipo === type)
}

const filterVaccinesBySearch = (list: VaccineDto[], searchTerm: string): VaccineDto[] => {
  if (!searchTerm.trim()) return list
  const term = searchTerm.toLowerCase()
  return list.filter(
    (item) => item.data.descricao?.toLowerCase().includes(term) || item.data.tipo?.toLowerCase().includes(term)
  )
}

const VaccineTabs: React.FC<VaccineTabsProps> = ({ vaccines, search, setSearch, selectedVacines, onSelect }) => {
  const styles = useStyles()
  const [activeTab, setActiveTab] = useState(0)

  const handleTabChange = useCallback((_: React.ChangeEvent<{}>, newValue: number) => {
    setActiveTab(newValue)
  }, [])

  const filteredByType = useMemo(() => {
    const currentType = TAB_LABELS[activeTab]?.type
    if (currentType === VACCINE_TYPES.ALL) {
      return vaccines
    }
    return filterVaccinesByType(vaccines, currentType as VaccineType)
  }, [vaccines, activeTab])

  const filteredList = useMemo(() => {
    return filterVaccinesBySearch(filteredByType, search)
  }, [filteredByType, search])

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value)
    },
    [setSearch]
  )

  const handleSelectVaccine = useCallback(
    (id: string) => {
      onSelect(id)
    },
    [onSelect]
  )

  const renderVaccineCard = useCallback(
    (vaccine: VaccineDto) => (
      <div
        key={vaccine.idVacina}
        className={styles.card}
        style={{ cursor: 'pointer' }}
        onClick={() => handleSelectVaccine(vaccine.idVacina!)}
      >
        <div onClick={(e) => e.stopPropagation()}>
          <InputCheck
            label=''
            id={vaccine.idVacina!}
            value={vaccine.idVacina!}
            checked={selectedVacines.includes(vaccine.idVacina!)}
            onChange={() => handleSelectVaccine(vaccine.idVacina!)}
          />
        </div>
        <div className={styles.info}>
          <span>{TAB_LABELS.find((tab) => tab.type === vaccine.data.tipo)?.label || 'Sem tipo'}</span>
          <span>Descrição: {vaccine.data.descricao || '-'}</span>
        </div>
      </div>
    ),
    [selectedVacines, handleSelectVaccine, styles.card, styles.info]
  )

  const renderList = useCallback((): React.ReactNode => {
    if (filteredList.length === 0) {
      return <span className={styles.notFound}>Nenhuma vacina encontrada</span>
    }

    return <div className={styles.listContainer}>{filteredList.map(renderVaccineCard)}</div>
  }, [filteredList, renderVaccineCard, styles.listContainer, styles.notFound])

  return (
    <div className={styles.root}>
      <div className={styles.inputContainer}>
        <Input label='Buscar vacinas' placeholder='Procurar...' value={search} onChange={handleSearchChange} />
      </div>

      <AppBar position='static' color='default'>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor='primary'
          textColor='primary'
          variant='scrollable'
          scrollButtons='auto'
          aria-label='tipos de vacinas'
        >
          {TAB_LABELS.map((tab, index) => (
            <Tab key={tab.label} label={tab.label} className={styles.tab} {...getTabA11yProps(index)} />
          ))}
        </Tabs>
      </AppBar>

      {TAB_LABELS.map((_, index) => (
        <TabPanel key={index} value={activeTab} index={index} className={styles.tabPanel}>
          {renderList()}
        </TabPanel>
      ))}
    </div>
  )
}

export default React.memo(VaccineTabs)
